import { FC, useContext, useEffect, useRef, useSyncExternalStore } from 'react';
import { MoContext } from 'contexts/MoContext';
import { SearchRequest } from 'ts/shared/interfaces/MOResponse';
import { finishSearchLoading } from 'utils/assistantChatStore';
import { highlightAssistantTargets } from 'utils/assistantHighlight';
import {
  applyOrdersSearch,
  getOrdersSearchVersion,
  peekOrdersSearch,
  subscribeOrdersSearch,
  takeOrdersSearch,
} from './ordersActions';

interface OrdersAssistantBridgeProps {
  ready: boolean;
  onSearch: (
    request: SearchRequest,
    view: { segmentText?: string | null },
  ) => Promise<boolean>;
}

const OrdersAssistantBridge: FC<OrdersAssistantBridgeProps> = ({ ready, onSearch }) => {
  const { searchModel, setSearchModel, modelOptions, setModelOptions, setMainFilter } =
    useContext(MoContext);
  const version = useSyncExternalStore(subscribeOrdersSearch, getOrdersSearchVersion);
  const onSearchRef = useRef(onSearch);
  const modelRef = useRef(searchModel);
  const optionsRef = useRef(modelOptions);
  onSearchRef.current = onSearch;
  modelRef.current = searchModel;
  optionsRef.current = modelOptions;

  useEffect(() => {
    const model = modelRef.current;
    const options = optionsRef.current;
    if (!ready || !model || !options || !peekOrdersSearch()) return;

    const action = takeOrdersSearch();
    if (!action) return;

    const applied = applyOrdersSearch(model, options, action);
    setSearchModel(applied.model);
    if (applied.options) setModelOptions(applied.options);
    if (applied.mainFilter !== undefined) setMainFilter(applied.mainFilter);
    highlightAssistantTargets(['search']);

    const request = applied.orderFilter
      ? { ...applied.model, orderFilter: applied.orderFilter }
      : applied.model;

    void onSearchRef.current(request, { segmentText: applied.segmentText }).then((ok) => {
      const finished = finishSearchLoading(0);
      const finishedOther = finishSearchLoading(2);
      if ((finished || finishedOther) && ok) highlightAssistantTargets(['list']);
    });
  }, [version, ready, setMainFilter, setModelOptions, setSearchModel]);

  return null;
};

export default OrdersAssistantBridge;
